import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiZap } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import PRICING_CONFIG from '../config/pricing';

/**
 * Subtle Credit Badge Component
 * 
 * Design Philosophy:
 * - Invisible when user has plenty of credits (>30%)
 * - Gentle reminder when getting low (10-30%)
 * - Prominent warning when almost out (<10%)
 * - Never shows during content creation to avoid "credit anxiety"
 */
export default function CreditBadge({ collapsed = false }) {
  const { user } = useAuth();
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchCredits();
      // Refresh credits every 30 seconds (silent background refresh)
      const interval = setInterval(fetchCredits, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const fetchCredits = async () => {
    try {
      const response = await api.getUserUsage(user.id);
      const usage = response.usage || {};
      const limits = response.limits || {};
      
      const used = usage.credits_used || 0;
      const limit = limits.credits_per_month || PRICING_CONFIG.FREE_CREDITS;
      const bonus = usage.bonus_credits || 0;
      const remaining = Math.max(0, limit - used) + bonus;
      
      setCredits({
        remaining,
        limit,
        bonus,
        percentage: limit > 0 ? ((limit - used) / limit) * 100 : 0
      });
    } catch (err) {
      console.error('Credit fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  // Don't show anything while loading or if no user
  if (loading || !user || !credits) {
    return null;
  }

  const { remaining, percentage } = credits;

  // Determine visibility and style based on credit level
  let badgeStyle = '';
  let showBadge = false;
  let tooltipText = '';

  if (percentage <= 10 || remaining <= 5) {
    // Almost out - prominent warning
    badgeStyle = 'bg-red-500/10 text-red-500 border-red-500/20';
    showBadge = true;
    tooltipText = 'Running low on credits';
  } else if (percentage <= 30 || remaining <= 15) {
    // Getting low - gentle reminder
    badgeStyle = 'bg-orange-500/10 text-orange-400 border-orange-500/20';
    showBadge = true;
    tooltipText = 'Credits getting low';
  } else {
    // Plenty of credits - very subtle or hidden
    badgeStyle = 'bg-secondary/50 text-muted-foreground border-transparent';
    showBadge = true; // Show but very subtle
    tooltipText = `${remaining} credits available`;
  }

  if (!showBadge) return null;

  // Collapsed sidebar - just show icon with number
  if (collapsed) {
    return (
      <Link 
        to="/usage" 
        className={`flex items-center justify-center p-2 rounded-lg border transition-colors hover:bg-secondary ${badgeStyle}`}
        title={tooltipText}
      >
        <div className="relative">
          <FiZap size={16} />
          {(percentage <= 30 || remaining <= 15) && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-current rounded-full animate-pulse" />
          )}
        </div>
      </Link>
    );
  }

  // Expanded sidebar - show credits
  return (
    <Link 
      to="/usage" 
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all hover:bg-secondary ${badgeStyle}`}
      title={tooltipText}
    >
      <FiZap size={14} className="flex-shrink-0" />
      <span className="text-sm font-medium">{remaining}</span>
      {(percentage <= 30 || remaining <= 15) && (
        <span className="text-xs opacity-70">credits</span>
      )}
    </Link>
  );
}

/**
 * Low Credit Warning Toast
 * Shows when user is running low - can be used anywhere in the app
 */
export function LowCreditWarning({ credits, onDismiss }) {
  if (!credits || credits.percentage > 30) return null;

  const isAlmostOut = credits.percentage <= 10 || credits.remaining <= 5;

  return (
    <div className={`fixed bottom-20 right-4 max-w-sm p-4 rounded-lg shadow-lg border z-50 animate-in slide-in-from-right ${
      isAlmostOut 
        ? 'bg-red-500/10 border-red-500/20 text-red-400' 
        : 'bg-orange-500/10 border-orange-500/20 text-orange-400'
    }`}>
      <div className="flex items-start gap-3">
        <FiZap className="mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-sm">
            {isAlmostOut ? 'Almost out of credits' : 'Credits running low'}
          </p>
          <p className="text-xs mt-1 opacity-80">
            {credits.remaining} credits remaining
          </p>
        </div>
        <Link 
          to="/usage" 
          className="text-xs font-medium hover:underline"
          onClick={onDismiss}
        >
          Get More →
        </Link>
      </div>
    </div>
  );
}
